import React, { createContext, useContext, useState, useEffect } from 'react';

const OnboardingContext = createContext();

export const useOnboarding = () => useContext(OnboardingContext);

// Helper function to get initial data from localStorage
const getInitialData = () => {
  try {
    const saved = localStorage.getItem('onboardingData');
    return saved ? JSON.parse(saved) : {
      personalInfo: {},
      businessDetails: {},
      address: {},
      msme: {},
      bank: {},
    };
  } catch (error) {
    console.error('Error loading onboarding data from localStorage:', error);
    return {
      personalInfo: {},
      businessDetails: {},
      address: {},
      msme: {},
      bank: {},
    };
  }
};

// Helper function to save data to localStorage
const saveToLocalStorage = (data) => {
  try {
    localStorage.setItem('onboardingData', JSON.stringify(data));
  } catch (error) {
    console.error('Error saving onboarding data to localStorage:', error);
  }
};

export const OnboardingProvider = ({ children }) => {
  const [onboardingData, setOnboardingData] = useState(getInitialData);
  const [currentStep, setCurrentStep] = useState(0);
  const [completedSteps, setCompletedSteps] = useState([]);
  const [formErrors, setFormErrors] = useState({});

  // Save to localStorage whenever data changes
  useEffect(() => {
    saveToLocalStorage(onboardingData);
  }, [onboardingData]);

  const updateSection = (section, data) => {
    setOnboardingData(prev => {
      const updated = {
        ...prev,
        [section]: { ...prev[section], ...data }
      };
      return updated;
    });
  };

  const updateCurrentStep = (step) => {
    setCurrentStep(step);
  };

  const markStepComplete = (step) => {
    setCompletedSteps(prev => {
      if (!prev.includes(step)) {
        return [...prev, step];
      }
      return prev;
    });
  };

  const markStepIncomplete = (step) => {
    setCompletedSteps(prev => prev.filter(s => s !== step));
  };

  const setFormError = (step, errors) => {
    setFormErrors(prev => ({
      ...prev,
      [step]: errors
    }));
  };

  const clearFormError = (step) => {
    setFormErrors(prev => {
      const newErrors = { ...prev };
      delete newErrors[step];
      return newErrors;
    });
  };

  const resetOnboarding = () => {
    setOnboardingData({
      personalInfo: {},
      businessDetails: {},
      address: {},
      msme: {},
      bank: {},
    });
    setCurrentStep(0);
    setCompletedSteps([]);
    setFormErrors({});
    localStorage.removeItem('onboardingData');
  };

  const getStepData = (step) => {
    const stepMap = {
      0: 'personalInfo',
      1: 'businessDetails',
      2: 'address',
      3: 'msme',
      4: 'bank'
    };
    return onboardingData[stepMap[step]] || {};
  };

  const isStepComplete = (step) => {
    return completedSteps.includes(step);
  };

  const getAllData = () => {
    return onboardingData;
  };

  const hasData = (section) => {
    return onboardingData[section] && Object.keys(onboardingData[section]).length > 0;
  };

  return (
    <OnboardingContext.Provider value={{
      onboardingData,
      updateSection,
      currentStep,
      updateCurrentStep,
      completedSteps,
      markStepComplete,
      markStepIncomplete,
      formErrors,
      setFormError,
      clearFormError,
      resetOnboarding,
      getStepData,
      isStepComplete,
      getAllData,
      hasData
    }}>
      {children}
    </OnboardingContext.Provider>
  );
};
